import { inject } from '@angular/core';
import { RedirectCommand, ResolveFn, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { catchError, EMPTY, map, mergeMap, Observable, of } from 'rxjs';
import { HttpError } from '@models/http-error.model';
import { MealPlanModel } from '@models/meal-plan/meal-plan.model';
import { CustomerService } from '@services/customer.service';
import { MealPlanService } from '@services/meal-plan.service';
import { MealmomentService } from '@services/meal-moment.service';
import { TerminalService } from '@services/terminal.service';
import { AppRoutes } from '@helpers/app-routes';

export const chooseMealPlanResolver: ResolveFn<MealPlanModel[]> = (): Observable<
  MealPlanModel[] | RedirectCommand
> => {
  const router = inject(Router);
  const mealPlanService = inject(MealPlanService);
  const mealmomentService = inject(MealmomentService);
  const toastr = inject(ToastrService);
  const translate = inject(TranslateService);
  const customer = inject(CustomerService).currentCustomer();

  if (!customer) {
    return of(new RedirectCommand(router.parseUrl(`/${AppRoutes.scanPage}`)));
  }

  const enterpriseId = inject(TerminalService).currentTerminal()!.enterprise_id;

  const cancelWithError = (message: string, titleKey: string): Observable<RedirectCommand> => {
    toastr.error(translate.instant(message), translate.instant(titleKey));
    return router.navigated
      ? EMPTY
      : of(new RedirectCommand(router.parseUrl(`/${AppRoutes.chooseOrderType}`)));
  };

  return mealPlanService.getMealPlans(customer.id, enterpriseId).pipe(
    map((mealPlans) => {
      const currentMealMomentId = mealmomentService.currentMealMoment()?.id;
      return mealPlans.filter((mealPlan) => mealPlan.meal_moment.id === currentMealMomentId);
    }),
    mergeMap((mealPlans) => {
      if (!mealPlans.length) {
        return cancelWithError('choose_meal_plan.empty', 'choose_meal_plan.errors.empty_title');
      }

      if (mealPlans.length === 1) {
        mealPlanService.setMealPlan(mealPlans[0]);
        return of(new RedirectCommand(router.parseUrl(`/${AppRoutes.order}`)));
      }

      return of(mealPlans);
    }),
    catchError((err: HttpError) =>
      cancelWithError(err.message, 'choose_meal_plan.errors.load_error_title'),
    ),
  );
};
